import React, { FC } from 'react';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import type { TrainingSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/trainingSearchResponse';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';

import RoverProtectCondensed from '../../../ServiceLanding/components/RoverProtectCondensed';
import SeoLocationPageWrapper from '../../components/SeoLocationPageWrapper';
import { PageSectionId, useSectionRef } from '../../hooks/useSectionRef';
import { dogTrainingHeroImage } from '../../images';

import DogTrainingFAQ from './components/DogTrainingFAQ';
import SeoLocationFooter from './components/SeoLocationFooter';
import TrainingCityServiceHero from './components/TrainingCityServiceHero';
import TrainingSeoLocationResultsCarousel from './components/TrainingSeoLocationResultsCarousel';

export type TrainingSeoLocationPageTemplateProps = {
  seoLocation: SeoLocation;
  searchResponse: TrainingSearchResponse;
  countryConfig: CountryConfiguration;
};

// TODO DEV-104195: Translate all text below

const TrainingSeoLocationPageTemplate: FC<TrainingSeoLocationPageTemplateProps> = ({
  seoLocation,
  searchResponse,
  countryConfig,
}) => {
  const faqsRef = useSectionRef(PageSectionId.FAQs);
  const howBrandWorksRef = useSectionRef(PageSectionId.HowBrandWorks);

  const cityName = seoLocation.details.name;
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <Flex flexDirection="column" alignItems="center">
          <TrainingCityServiceHero
            seoLocationDetails={seoLocation.details}
            heroImage={dogTrainingHeroImage}
          />
        </Flex>
      </Section>
      <RoverProtectCondensed countryConfig={countryConfig} isTraining my={['6x', '12x']} />

      {searchResponse.results.length > 0 && (
        <Section paddingLeft="4x" mb={['16x', '12x']}>
          <TrainingSeoLocationResultsCarousel
            searchResponse={searchResponse}
            heading={`Popular ${cityName} dog trainers`}
            searchFilters={searchResponse.query}
            countryConfig={countryConfig}
          />
        </Section>
      )}

      <HowBrandWorks htmlRef={howBrandWorksRef}>
        <HowBrandWorks.Display>How dog training on Rover works</HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>Search</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Read verified reviews and choose a trainer who’s a great match for you and your dog.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>Chat with your trainer</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Talk about your training goals and find a time that works for you.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>Book training sessions</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              All payments use Rover’s secured platform and bookings are backed by 24/7 support.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>
      <Section paddingLeft="4x" mb={['16x', '12x']}>
        <DogTrainingFAQ
          locationName={cityName}
          localizedLinkUrls={localizedLinkUrls}
          htmlRef={faqsRef}
        />
        <SeoLocationFooter
          linkedLocations={seoLocation.linkedLocations}
          topNeighborhoodServices={seoLocation.topNeighborhoodServices}
          seoLocation={seoLocation.details}
          countryConfigurationUrls={countryConfig.urls}
        />
      </Section>
    </SeoLocationPageWrapper>
  );
};

export default TrainingSeoLocationPageTemplate;
